import '@mobiscroll/react/dist/css/mobiscroll.min.css'
import {
  Eventcalendar,
  MbscCalendarDayData,
  MbscCalendarEventData,
  MbscEventDragEvent,
} from '@mobiscroll/react'
import { useState, useCallback, useMemo, useEffect } from 'react'
import {
  RotaBuilderContentWrapper,
  RotaWrapper,
} from '../../common/components/commonStyledComponents'
import { CalendarOptions } from '../../common/constants'
import Hour from '../../common/components/eventcalendar/Hour'
import Day from '../../common/components/eventcalendar/Day'
import Event from '../../common/components/eventcalendar/Event'
import {
  getDateWithoutUTC,
  BACKEND_REQUEST_STRING_FORMAT,
  YEAR_FIRST_DATE_FORMAT,
} from 'screens/Rostering/utils.datetime'
import { useQueryClient } from 'react-query'
import StartRotaPublishModal from '../../common/components/modals/StartRotaPublishModal'
import dayjs from 'dayjs'
import StartRotaOverlay from '../../common/components/StartRotaOverlay'
import Resource from '../../common/components/eventcalendar/Resource'
import EventCalendarHeader from '../../common/components/eventcalendar/EventcalendarHeader'
import { CountChangesResponse } from 'screens/Rostering/hooks/useCountChanges'
import useRevisionUpdate from 'screens/Rostering/hooks/useRevisionUpdate'
import useRevisionRollback from 'screens/Rostering/hooks/useRevisionRollback'
import useRotaGenerated from 'screens/Rostering/hooks/useRotaGenerated'
import { RotaBuilderFilters } from '../../common'
import {
  ALLOCATION_LIST_QUERY_KEY,
  INSTANCE_QUERY_KEY,
  INSTANCES_QUERY_KEY,
  REVISION_QUERY_KEY,
  REVISIONS_QUERY_KEY,
} from 'screens/Rostering/hooks/queryConstants'
import { DAILY, SHIFT, WEEKLY } from 'screens/Rostering/constants'
import { getWarningsListForEvent } from '../../common/components/eventcalendar/utils'
import { useTranslation } from 'react-i18next'
import { RotaDrawer } from 'screens/Rostering/RotaDrawer'
import { useDrawerData } from 'screens/Rostering/hooks/useDrawerData'
import useFeatureFlags from 'hooks/useFeatureFlags'
import {
  ResidentResource,
  RotaBuilderFiltersType,
  RotaResidentEventInstance,
  RotaInstanceStatus,
  START_OVERLAY_TYPE,
} from 'screens/Rostering/types'
import NewRotaPatternModal from 'screens/Rostering/RotaPattern/NewRotaPatternModal/NewRotaPatternModal'
import { useShiftTypesList } from 'screens/Rostering/hooks/useShiftTypesList'
import PublishedRotaOverlay from '../../common/components/PublishedRotaOverlay'
import { showToast } from 'common/utils'
import { message } from 'antd'
import UndoButton from '../../common/components/UndoButton'

type ShiftRotaProps = {
  selectedDate: Date
  setSelectedDate: any
  timelineView: any
  setTimelineView: (view: any) => void
  resources: any[]
  events: RotaResidentEventInstance[]
  isLoading: boolean
  publishedRota?: boolean
  filterParams?: RotaBuilderFiltersType
  onFilterChange?: (value: RotaBuilderFiltersType) => void
  isDailyView: boolean
  refetchCountChanges?: () => void
  countChangesData: CountChangesResponse
  hasFilters?: boolean
  calendarOptions?: typeof CalendarOptions
  showAllEvents?: boolean
  onToggleShowAllEvents?: () => void
}

export function ShiftRota({
  selectedDate,
  setSelectedDate,
  timelineView,
  setTimelineView,
  resources,
  events,
  isLoading,
  publishedRota,
  filterParams: filterParamsWithoutShift,
  onFilterChange,
  isDailyView,
  refetchCountChanges,
  countChangesData,
  hasFilters,
  calendarOptions,
  showAllEvents,
  onToggleShowAllEvents,
}: ShiftRotaProps) {
  const { t } = useTranslation()

  const flags = useFeatureFlags({
    flagNames: [
      'rostering_parked_features',
      'new_create_pattern',
      'rostering_builder_drag_and_drop',
    ],
  })?.data

  const flagsRosteringParkedFeaturesEnabled =
    flags?.rostering_parked_features?.enabled

  const flagsNewCreatePatternEnabled = flags?.new_create_pattern?.enabled

  const flagsDragAndDropEnabled =
    flags?.rostering_builder_drag_and_drop?.enabled

  const [isZoomed, setIsZoomed] = useState(false)

  // Create view object with dynamic calendar options
  const view = useMemo(() => {
    const currentCalendarOptions = calendarOptions || CalendarOptions
    let dynamicTimelineView = timelineView

    // Override timelineView with our dynamic calendar options
    if (timelineView?.type === 'week') {
      dynamicTimelineView = currentCalendarOptions.weekly
    } else if (timelineView?.type === 'day') {
      if (isZoomed) {
        dynamicTimelineView = currentCalendarOptions.dailyZoomed
      } else {
        dynamicTimelineView = currentCalendarOptions.daily
      }
    }

    return { timeline: dynamicTimelineView }
  }, [timelineView, calendarOptions, isZoomed])

  const queryClient = useQueryClient()

  const {
    drawerVisible,
    setDrawerVisible,
    drawerAllocatedTeamVisible,
    setDrawerAllocatedTeamVisible,
    setDrawerDrawerOriginalDataId,
    drawerOriginalDataId,
  } = useDrawerData()
  const filterParams = useMemo(
    () => ({ ...filterParamsWithoutShift, siteShift: true }),
    [filterParamsWithoutShift],
  )

  const { updateRevision } = useRevisionUpdate({
    selectedDate,
    intervalType: isDailyView ? DAILY : WEEKLY,
    onUpdateSuccess: () => {
      refetchCountChanges?.()
      queryClient?.invalidateQueries([INSTANCES_QUERY_KEY])
      queryClient?.invalidateQueries([INSTANCE_QUERY_KEY])
      queryClient?.invalidateQueries([REVISION_QUERY_KEY])
      queryClient?.invalidateQueries([REVISIONS_QUERY_KEY])
      queryClient?.invalidateQueries([ALLOCATION_LIST_QUERY_KEY])
    },
  })

  const handleUndoWithEventId = async (eventId: string) => {
    // Set the event ID and then use the existing rollback function
    setLastDraggedEventId(eventId)

    // Small delay to let the hook update
    setTimeout(async () => {
      if (!rollbackRevision) return

      try {
        // Hide all existing toasts first
        message.destroy()

        await rollbackRevision()

        setLastDraggedEventId(null)
        showToast({
          title: t('CHANGE_REVERTED_SUCCESSFULLY'),
          type: 'success',
        })
      } catch (error) {
        showToast({
          title: t('FAILED_TO_UNDO_CHANGE'),
          type: 'error',
        })
      }
    }, 100)
  }

  const onCalendarChange = useCallback(
    (event: any) => {
      const val = event.target.value
      if (val === 'weekly') {
        setTimelineView(CalendarOptions.weekly)
      } else if (val === DAILY) {
        setTimelineView(CalendarOptions.daily)
        if (dayjs(selectedDate).isSame(dayjs(), 'isoWeek')) {
          setSelectedDate(new Date())
        }
      }
    },
    [selectedDate, setSelectedDate, setTimelineView],
  )

  const handleZoom = () => {
    setIsZoomed(!isZoomed)
    setTimelineView(
      isZoomed ? CalendarOptions.daily : CalendarOptions.dailyZoomed,
    )
  }

  const renderResource = useCallback(
    (resource: ResidentResource) => (
      <Resource resource={resource} type={SHIFT} />
    ),
    [],
  )

  const renderScheduleEvent = useCallback(
    (args: MbscCalendarEventData) => (
      <Event
        // @ts-ignore
        args={args}
        type={SHIFT}
        instanceWarnings={countChangesData?.warnings?.instanceWarningMaps}
        warnings={getWarningsListForEvent(
          countChangesData?.warnings?.instanceWarningMaps,
          args?.original?.id as unknown as string,
          args?.original?.teamMembers,
          t,
          flagsRosteringParkedFeaturesEnabled,
        )}
        setDrawerAllocatedTeamVisible={(flag) => {
          setDrawerDrawerOriginalDataId(args?.original?.id)
          setDrawerAllocatedTeamVisible(flag)
        }}
        setDrawerVisible={(flag) => {
          setDrawerDrawerOriginalDataId(args?.original?.id)
          setDrawerVisible(flag)
        }}
        isDailyView={isDailyView}
        publishedRota={publishedRota}
      />
    ),
    [countChangesData?.warnings, events, isDailyView, publishedRota],
  )

  const renderHour = (args: MbscCalendarDayData) => {
    return <Hour date={args.date} />
  }

  const renderDay = (args: MbscCalendarDayData) => {
    return <Day date={args.date} isDailyView={isDailyView} />
  }

  const renderHeader = useCallback(() => {
    return (
      <EventCalendarHeader
        selectedDate={selectedDate}
        onCalendarChange={onCalendarChange}
        handleZoom={handleZoom}
        isZoomed={isZoomed}
        isDailyView={isDailyView}
        countChangesData={countChangesData}
        publishedRota={publishedRota}
        filterParams={filterParams}
        showAllEvents={showAllEvents}
        onToggleShowAllEvents={onToggleShowAllEvents}
      />
    )
  }, [
    selectedDate,
    onCalendarChange,
    handleZoom,
    timelineView,
    isZoomed,
    isDailyView,
    countChangesData,
    publishedRota,
    filterParams,
  ])

  const [originalEventState, setOriginalEventState] = useState<{
    start: string | Date
    end: string | Date
  } | null>(null)

  // State to track last dragged event for undo functionality
  const [lastDraggedEventId, setLastDraggedEventId] = useState<string | null>(
    null,
  )

  const { rollbackRevision } = useRevisionRollback({
    id: lastDraggedEventId || undefined,
  })

  const handleDragStart = (e: any) => {
    // Store original state before drag
    setOriginalEventState({
      start: e.event.start,
      end: e.event.end,
    })
  }

  const handleDragEvent = async (e: MbscEventDragEvent): Promise<boolean> => {
    // Check for restricted event statuses (matching drawer restrictions)
    const isInProgress =
      e.event.status === RotaInstanceStatus.IN_PROGRESS ||
      e.event.clockIns?.length > 0
    const isDone = e.event.status === RotaInstanceStatus.DONE
    const isOverdue = e.event.status === RotaInstanceStatus.OVERDUE
    const isCancelled = e.event.status === RotaInstanceStatus.CANCELLED

    const isInvalidStatusDrop =
      isInProgress || isDone || isOverdue || isCancelled

    if (isInvalidStatusDrop) {
      let errorMessage = t('CANNOT_EDIT_ALLOCATION_STATUS')

      if (isInProgress) {
        errorMessage = t('CANNOT_EDIT_IN_PROGRESS_EVENT')
      } else if (isOverdue) {
        errorMessage = t('CANNOT_EDIT_OVERDUE_EVENT')
      }

      showToast({
        title: errorMessage,
        type: 'error',
      })

      // Revert event to original position
      if (originalEventState) {
        e.event.start = originalEventState.start
        e.event.end = originalEventState.end
      }
      setOriginalEventState(null)
      return false
    }

    // Store original state for undo (from drag start)
    let undoData: any = null
    if (originalEventState) {
      undoData = {
        eventId: e?.event?.id?.toString() || '',
        originalStart: originalEventState.start,
        originalEnd: originalEventState.end,
      }
    }

    try {
      const updatePayload: any = {
        eventId: e?.event?.id?.toString() || '',
        startDate: getDateWithoutUTC(
          e?.event?.start as string | Date,
          BACKEND_REQUEST_STRING_FORMAT,
        ),
        endDate: getDateWithoutUTC(
          e?.event?.end as string | Date,
          BACKEND_REQUEST_STRING_FORMAT,
        ),
      }

      // Add undo action for drag operations (only when feature flag is enabled)
      if (originalEventState && flagsDragAndDropEnabled) {
        const eventId = e?.event?.id?.toString() || ''
        setLastDraggedEventId(eventId)

        updatePayload.undoAction = (
          <UndoButton onClick={() => handleUndoWithEventId(eventId)} />
        )
      }

      await updateRevision?.(updatePayload)
      setOriginalEventState(null) // Clear original state
      return true
    } catch (error) {
      showToast({
        title: t('ERROR_UPDATING_EVENT'),
        type: 'error',
      })
      setOriginalEventState(null) // Clear original state
      return false
    }
  }

  const handleSelectedDateChange = useCallback((args: any) => {
    setSelectedDate(args.date)
  }, [])

  const { checkRotaGenerated } = useRotaGenerated()

  const [isInitialized, setIsInitialized] = useState(false)
  const [hasEvents, setHasEvents] = useState(false)

  const [showStartRotaModal, setShowStartRotaModal] = useState(false)

  const openStartRotaModal = () => {
    setShowStartRotaModal(true)
  }

  const closeStartRotaModal = () => {
    setShowStartRotaModal(false)
  }

  useEffect(() => {
    let isMounted = true
    async function fetchHasRota() {
      const hasRota = await checkRotaGenerated({
        patternStartDate: dayjs(selectedDate)
          .startOf('isoWeek')
          .format(YEAR_FIRST_DATE_FORMAT),
        patternEndDate: dayjs(selectedDate)
          .endOf('isoWeek')
          .format(YEAR_FIRST_DATE_FORMAT),
      })

      if (isMounted) {
        setHasEvents(events.length > 0 || hasRota)
        setIsInitialized(true)
      }
    }
    fetchHasRota()
    return () => {
      isMounted = false
    }
  }, [selectedDate])

  const [clickedDate, setClickedDate] = useState<Date | null>(null)
  const [clickedTimeStart, setClickedTimeStart] = useState<string | null>(null)
  const [clickedTimeEnd, setClickedTimeEnd] = useState<string | null>(null)
  const [showPatternModal, setShowPatternModal] = useState(false)
  const { shiftTypes } = useShiftTypesList()

  const handleCellClick = (event: any) => {
    const date = new Date(event.date)
    setClickedDate(date)

    if (isDailyView) {
      const clickedHour = date.getHours()
      const clickedMinute = date.getMinutes()

      const startTime = `${String(clickedHour).padStart(2, '0')}:${String(clickedMinute).padStart(2, '0')}`

      const endHour = (clickedHour + 1) % 24
      const endTime = `${String(endHour).padStart(2, '0')}:${String(clickedMinute).padStart(2, '0')}`

      setClickedTimeStart(startTime)
      setClickedTimeEnd(endTime)
    } else {
      setClickedTimeStart(null)
      setClickedTimeEnd(null)
    }

    if (event.resource) {
      setShowPatternModal(true)
    }
  }

  const getInitialValues = () => {
    const values: any = {
      reoccurenceTooglePattern: false,
    }

    if (clickedDate) {
      values.patternStart = dayjs(clickedDate)
    }

    if (shiftTypes && shiftTypes.length > 0) {
      values.instanceTypeId = shiftTypes[0].id
    }

    if (isDailyView && clickedTimeStart && clickedTimeEnd) {
      values.startTime = clickedTimeStart
      values.endTime = clickedTimeEnd
    }

    return values
  }

  return (
    <RotaWrapper>
      <RotaBuilderFilters
        builderType={SHIFT}
        value={filterParams}
        onChange={onFilterChange}
        publishedRota={publishedRota}
      />
      <RotaBuilderContentWrapper>
        <Eventcalendar
          firstDay={1}
          timeFormat="HH:mm"
          className="eventcalendar"
          dragToMove={!publishedRota && isDailyView && flagsDragAndDropEnabled}
          eventDelete={!publishedRota}
          dragInTime={!publishedRota && isDailyView && flagsDragAndDropEnabled}
          dragToResize={
            !publishedRota && isDailyView && flagsDragAndDropEnabled
          }
          showEventTooltip={false}
          dragBetweenResources={false}
          clickToCreate={false}
          dragToCreate={false}
          view={view}
          data={events}
          resources={resources}
          renderDay={renderDay}
          renderHour={renderHour}
          renderEvent={renderScheduleEvent}
          renderScheduleEvent={renderScheduleEvent}
          renderResource={renderResource}
          renderHeader={renderHeader}
          onEventDragStart={handleDragStart}
          onEventDragEnd={handleDragEvent}
          selectedDate={selectedDate}
          onSelectedDateChange={handleSelectedDateChange}
          onCellClick={
            flagsNewCreatePatternEnabled ? handleCellClick : undefined
          }
        />
        {!publishedRota && isInitialized && (
          <StartRotaOverlay
            hasEvents={hasEvents}
            isLoading={isLoading}
            onStartRota={openStartRotaModal}
            type={START_OVERLAY_TYPE.NO_PATTERNS}
          />
        )}
        {publishedRota && events.length === 0 && hasFilters && (
          <PublishedRotaOverlay isLoading={isLoading} />
        )}
      </RotaBuilderContentWrapper>
      {showPatternModal && (
        <NewRotaPatternModal
          onCancel={() => setShowPatternModal(false)}
          type={SHIFT}
          initialValues={getInitialValues()}
        />
      )}
      {showStartRotaModal && (
        <StartRotaPublishModal
          open={showStartRotaModal}
          onClose={closeStartRotaModal}
          fromDate={dayjs(selectedDate)
            .startOf('isoWeek')
            .format(YEAR_FIRST_DATE_FORMAT)}
          toDate={dayjs(selectedDate)
            .endOf('isoWeek')
            .format(YEAR_FIRST_DATE_FORMAT)}
        />
      )}
      {drawerVisible && (
        <RotaDrawer
          onClose={() => {
            setDrawerVisible(false)
            setDrawerDrawerOriginalDataId(undefined)
          }}
          isVisible={drawerVisible}
          id={drawerOriginalDataId}
          publishedRota={publishedRota}
        />
      )}
      {drawerAllocatedTeamVisible && (
        <RotaDrawer
          onClose={() => {
            setDrawerAllocatedTeamVisible(false)
            setDrawerDrawerOriginalDataId(undefined)
          }}
          isVisible={drawerAllocatedTeamVisible}
          id={drawerOriginalDataId}
          defaultTab={2}
          publishedRota={publishedRota}
        />
      )}
    </RotaWrapper>
  )
}

export default ShiftRota
